import React, { useEffect } from "react";
import styles from "./OfficeWorkFeeMasterDialog.module.scss";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DehazeIcon from "@mui/icons-material/Dehaze";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  ResponderProvided,
} from "react-beautiful-dnd";
import { getBrandInvoices } from "../../../../apis/brand-api";
import { InvoiceListResponseData } from "../../../../apis/models/InvoiceMasterRequest";
import {
  getOfficeWorkFeeOptions,
  postOfficeWorkFeeOptions,
} from "../../../../apis/invoice-api";
import {
  OfficeWorkOption,
} from "../../../../apis/models/OfficeWorkResponse";
import { InvStatus } from "../../../../libs/constants";
import InputCell from "../../../core/InputCell";
import {
  OfficeWorkOptionRequestItem,
  OfficeWorkOptionRequest,
} from "../../../../apis/models/OfficeWorkRequest";

type RowType = {
  isAddedRow?: boolean;
  order?: number;
} & OfficeWorkOption;

type WorkDetailsType = {
  officeWorkFeeOptionId?: string;
  content: string;
  unitPrice: number;
}

type displayRowType = {
  officeWorkFeeOptionId?: string;
  category: string;
  unitPrice: number;
  workDetails: WorkDetailsType[];
}

const COL_WORK = "work";
const COL_PRICE = "price";
const COL_CATEGORY = "category";


const OfficeWorkFeeMasterDialog: React.FC<OfficeWorkFeeMasterDialogProps> = (
  props: OfficeWorkFeeMasterDialogProps
) => {
  const { open, brandNo, invoiceMasterId, onClose, onSuccess, onCancel } =
    props;
  const sortData = React.useRef<{ col: string; asc: boolean }>({
    col: "",
    asc: false,
  });

  const [masterDataList, setMasterDataList] = React.useState<RowType[]>([]);
  const [invoices, setInvoices] = React.useState<InvoiceListResponseData[]>([]);
  const [saveDisabled, setSaveDisabled] = React.useState<boolean>(true);
  const [updating, setUpdating] = React.useState<boolean>(false);
  const [displayDataList, setDisplayDataList] = React.useState<displayRowType[]>([]);

  useEffect(() => {
    let canceled = false;
    getBrandInvoices(brandNo)
      .then(function (invoiceResponse) {
        const brandInvoices: Array<InvoiceListResponseData> =
          invoiceResponse.data.data;
        if (!brandInvoices || brandInvoices.length <= 0 || canceled) {
          return;
        }

        setInvoices(
          brandInvoices
            .filter((inv) => inv.status === InvStatus.NOT_CONFIRMED)
            .sort((a, b) => (a.to < b.to ? -1 : 1))
        );

        getOfficeWorkFeeOptions(invoiceMasterId)
          .then(function (masterResponse) {
            const officeWorkFeeOptions: Array<OfficeWorkOption> = masterResponse
              .data.data
              ? masterResponse.data.data
              : [];
            if (!canceled) {
              setMasterDataList(
                officeWorkFeeOptions.sort((a, b) => a.order! - b.order!)
              );
            }
          })
          .catch(function (error) {
            console.log(error);
          });
      })
      .catch(function (error) {
        console.log(error);
      });

    return () => {
      canceled = true;
    };
  }, [brandNo, invoiceMasterId]);

  const isRowError = (data: displayRowType): boolean => {
    if(data.workDetails.length > 0) {
      for (let i = 0; i < data.workDetails.length; i++) {
        const obj = data.workDetails[i];
        if(obj.content === "" || obj.unitPrice <= 0) {
          return true;
        }
      }
    } else {
      return data.unitPrice <= 0 || data.category === ""
    }
    return false
  }

  const masterDataListToDisplayDataList = (masterDataList: RowType[]): displayRowType[] => {
    const returnData: displayRowType[] = []
    masterDataList.forEach(data => {
      const isCategory = data.officeWorkFeeOption.category === data.officeWorkFeeOption.workDetails;
      if (isCategory) {
        returnData.push({
          officeWorkFeeOptionId: data.officeWorkFeeOptionId,
          category: data.officeWorkFeeOption.category,
          unitPrice: data.officeWorkFeeOption.unitPrice,
          workDetails: []
        })
      } else {
        const parent = returnData.find(d => d.category === data.officeWorkFeeOption.category);
        if (parent) {
          parent.workDetails?.push({
            officeWorkFeeOptionId: data.officeWorkFeeOptionId,
            content: data.officeWorkFeeOption.workDetails,
            unitPrice: data.officeWorkFeeOption.unitPrice,
          })
        } else {
          console.error("category not found [" + data.officeWorkFeeOption.category + "]");
        }
      }
    })
    return returnData
  }

  useEffect(() => {
      setDisplayDataList(masterDataListToDisplayDataList(masterDataList))
  }, [masterDataList]);

  useEffect(() => {
    setSaveDisabled(
      displayDataList.findIndex((master, index) => {
        const hasError = isRowError(master);
        if (hasError) {
          console.warn("入力エラーを検知：row_number=" + index)
        }
        return hasError
      }) >= 0
    );
  }, [displayDataList])

  const onSort = (colName: string) => {
    if (sortData.current.col === colName) {
      sortData.current.asc = !sortData.current.asc;
    } else {
      sortData.current.col = colName;
      sortData.current.asc = true;
    }

    if (colName === COL_CATEGORY) {
      setMasterDataList((prev) =>
        [...prev].sort(
          (a, b) =>
            a.officeWorkFeeOption.category.localeCompare(
              b.officeWorkFeeOption.category
            ) * (sortData.current.asc ? 1 : -1)
        )
      );
    } else if (colName === COL_PRICE) {
      setMasterDataList((prev) =>
        [...prev].sort(
          (a, b) =>
            (a.officeWorkFeeOption.unitPrice - b.officeWorkFeeOption.unitPrice) *
            (sortData.current.asc ? 1 : -1)
        )
      );
    }
  };

  const onDragEnd = (result: DropResult, provide: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    if (result.type === "category") {
      const targetData = displayDataList[result.source.index];
      const newData = [...displayDataList];
      newData.splice(result.source.index, 1);
      newData.splice(result.destination.index, 0, targetData);
      setDisplayDataList(newData);
    }else {
      const targetData = displayDataList[parseInt(result.type)].workDetails![result.source.index];
      const newData = [...displayDataList];
      newData[parseInt(result.type)].workDetails!.splice(result.source.index, 1);
      newData[parseInt(result.type)].workDetails!.splice(result.destination.index, 0, targetData);
      setDisplayDataList(newData);
    }
  };

  const onAddRow = () => {
    const data: displayRowType = {
      category: "",
      unitPrice: 0,
      workDetails: []
    };
    setDisplayDataList((prev) => [data].concat(prev));
  };

  const onAddWorkDetailsRow = (category: string, index: number) => {
    const data = {
      content: "",
      unitPrice: 0
    }
    if (displayDataList[index].workDetails.length > 0) {
      setDisplayDataList((prev) => {
        const newData = [...prev]
        newData[index].workDetails.splice(0, 0, data)
        return newData
      });
    }else {
      setDisplayDataList((prev) => {
        const newData = [...prev]
        newData[index].unitPrice = 0
        newData[index].workDetails = [data]
        return newData
      });
    }
  };

  const onDeleteRow = (rowNumber: number, isCategory: boolean, workDetailRowNumber?: number) => {
    if(isCategory) {
      setDisplayDataList((prev) =>
        [...prev].filter((master, index) => index !== rowNumber)
      );
    }else {
      if (displayDataList[rowNumber].workDetails.length > 0 && workDetailRowNumber !== undefined) {
        setDisplayDataList((prev) => {
          const newData = [...prev];
          newData[rowNumber].workDetails.splice(workDetailRowNumber,1)
          if(newData[rowNumber].workDetails.length === 0) {
            newData[rowNumber].workDetails = []
          }
          return newData
        })
      }
    }
  };

  const onChangeRowData = (
    target: string,
    data: string | number | [],
    rowNumber: number,
    workDetailRowNumber?: number
  ) => {
    setDisplayDataList((prev) => {
      const newData = [...prev];
      if (workDetailRowNumber === undefined) {
        if (target === COL_WORK) {
          newData[rowNumber].workDetails = data as [];
        } else if (target === COL_PRICE) {
          newData[rowNumber].unitPrice = data as number;
        } else if (target === COL_CATEGORY) {
          newData[rowNumber].category = data as string;
        }
      } else {
        if (target === COL_WORK && newData[rowNumber].workDetails !== undefined) {
          newData[rowNumber].workDetails![workDetailRowNumber].content = data as string;
        } else if (target === COL_PRICE  && newData[rowNumber].workDetails !== undefined) {
          newData[rowNumber].workDetails![workDetailRowNumber].unitPrice = data as number;
        }
      }
      return newData;
    });
  };

  const onSave = () => {
    let officeWorkFeeOptions: OfficeWorkOptionRequestItem[] = [];
    let order = 1
    for (const obj of displayDataList) {
      if (obj.workDetails.length === 0) {
        const data: OfficeWorkOptionRequestItem = {
          id: obj.officeWorkFeeOptionId,
          unitPrice: Number(obj.unitPrice),
          workDetails: obj.category,
          order: order,
          category: obj.category,
        };
        officeWorkFeeOptions.push(data);
        order++;
      } else {
        const data: OfficeWorkOptionRequestItem = {
          id: obj.officeWorkFeeOptionId,
          unitPrice: 0,
          workDetails: obj.category,
          order: order,
          category: obj.category,
        };
        officeWorkFeeOptions.push(data);
        order++;

        for (const workDetailsObj of obj.workDetails) {
          const data: OfficeWorkOptionRequestItem = {
            id: workDetailsObj.officeWorkFeeOptionId,
            unitPrice: Number(workDetailsObj.unitPrice),
            workDetails: workDetailsObj.content,
            order: order,
            category: obj.category,
          };
          officeWorkFeeOptions.push(data);
          order++;
        }
      }
    }

    const req: OfficeWorkOptionRequest = {
      officeWorkFeeOptions: officeWorkFeeOptions,
      reflect: true
    };

    setUpdating(true);
    postOfficeWorkFeeOptions(invoiceMasterId, req)
      .then(function () {
        onSuccess();
      })
      .catch(function (error) {
        console.log(error);
        alert("保存に失敗しました。");
      })
      .finally(() => setUpdating(false));
  };


  return (
    <Dialog open={open} onClose={() => !updating && onClose()} maxWidth="xl">
      <DialogTitle
        sx={{ fontWeight: 700, fontSize: "24px", padding: "40px 40px 24px" }}
      >
        事務作業内容管理
      </DialogTitle>
      <DialogContent sx={{ padding: "0 40px 24px" }}>
        <div className={styles.notes}>※カテゴリーを削除するとカテゴリーに紐づく作業内容も削除されます。</div>
        <div className={styles.result}>
          {updating ? (
            <div>
              <CircularProgress />
            </div>
          ) : (
            <div>
              <div className={styles.table_wrapper}>
                <div className={styles.result_table}>
                  <div className={styles.header}>
                    <span>単価</span>
                  </div>
                  <DragDropContext
                    onDragEnd={(result, provide) => onDragEnd(result, provide)}
                  >
                    <Droppable droppableId="droppable" type="category">
                      {(provided, snapshot) => (
                        <div 
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {displayDataList.map((item, index) => (
                            <Draggable
                              key={`draggableId${index}`}
                              draggableId={`draggableId${index}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <div className={styles.body}>
                                    <span
                                      className={styles.result_col1}
                                    >
                                      <span {...provided.dragHandleProps}>
                                        <DehazeIcon color="primary" />  
                                      </span>
                                    </span>
                                    <span className={styles.result_col2}>
                                      <Stack direction="row">
                                        <InputCell
                                          value={item.category}
                                          type={"text"}
                                          onChangeText={(data) =>
                                            onChangeRowData(COL_CATEGORY, data, index)
                                          }
                                          isConfirmed={false}
                                          textAlignment="left"
                                        />
                                        <div className={styles.addWorkDetails_button}>
                                          <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => onAddWorkDetailsRow(item.category, index)}
                                          >
                                            ＋作業内容追加
                                          </Button>
                                        </div>
                                      </Stack>
                                    </span>
                                    <span className={styles.result_col3}>
                                      {
                                        item.workDetails.length > 0 ?
                                          "-"
                                        :<InputCell
                                          value={item.unitPrice}
                                          type={"number"}
                                          onChangeText={(data) =>
                                            onChangeRowData(COL_PRICE, data, index)
                                          }
                                          isConfirmed={false}
                                          textAlignment="right"
                                        />
                                      }
                                    </span>
                                    <span
                                      className={styles.result_col4}
                                      onClick={() => onDeleteRow(index, true)}
                                    >
                                      <CloseIcon
                                        color="primary"
                                        fontSize="small"
                                        className={styles.delete_icon}
                                      />
                                    </span>
                                  </div>
                                  { item.workDetails !== undefined ? 
                                  <Droppable droppableId={`droppable${index}`} type={`${index}`}>
                                    {provided => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                      >
                                        { item.workDetails !== undefined &&
                                          item.workDetails.map((workDetailItem, workDetailIndex) => {
                                            return (
                                            <Draggable
                                              key={`${index}${workDetailIndex}`}
                                              draggableId={`${index}${workDetailIndex}`}
                                              index={workDetailIndex}
                                            >
                                              {provided => (
                                                <div
                                                  className={styles.body}
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                >
                                                  <span 
                                                    className={`${styles.result_col1} ${styles.cursor_default}`}
                                                  ></span>
                                                  <span 
                                                    className={styles.result_col2}
                                                  >
                                                    <Stack direction="row">
                                                      <span  {...provided.dragHandleProps}>
                                                        <DehazeIcon color="primary"/>
                                                      </span>
                                                      <span className={styles.result_col2_content}>
                                                        <InputCell
                                                          value={workDetailItem.content}
                                                          type={"text"}
                                                          onChangeText={(data) =>
                                                            onChangeRowData(COL_WORK, data, index, workDetailIndex)
                                                          }
                                                          isConfirmed={false}
                                                          textAlignment="left"
                                                        />
                                                      </span>
                                                    </Stack>
                                                  </span>
                                                  <span className={styles.result_col3}>
                                                    <InputCell
                                                      value={workDetailItem.unitPrice}
                                                      type={"number"}
                                                      onChangeText={(data) =>
                                                        onChangeRowData(COL_PRICE, data, index, workDetailIndex)
                                                      }
                                                      isConfirmed={false}
                                                      textAlignment="right"
                                                    />
                                                  </span>
                                                  <span 
                                                    className={styles.result_col4}
                                                    onClick={() => onDeleteRow(index, false, workDetailIndex)}
                                                  >
                                                    <CloseIcon
                                                      color="primary"
                                                      fontSize="small"
                                                      className={styles.delete_icon}
                                                    />
                                                  </span>
                                                </div> 
                                              )}
                                            </Draggable>
                                          )})
                                        }
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                  :<></>
                                  }
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
              <div className={styles.information}>
              {invoices.length > 0 ? (
                  <div>
                    <div>下記の請求にも変更が反映される。</div>
                    {invoices.map((item, index) => (
                      item.year !== null && item.month !== null ?
                      <div
                        key={index}
                      >{`${item.year}年${item.month}月請求`}</div>
                      :<div></div>
                    ))}
                  </div>
                ) : (
                  <div></div>
                )}
                <div className={styles.add_button}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => onAddRow()}
                  >
                    ＋カテゴリー追加
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className={styles.functions}>
            <Button
              variant="outlined"
              onClick={() => onCancel()}
              className={styles.btn}
              disabled={updating}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={() => onSave()}
              className={styles.btn}
              disabled={saveDisabled || updating}
            >
              保存
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfficeWorkFeeMasterDialog;

export type OfficeWorkFeeMasterDialogProps = {
  open: boolean;
  brandNo: string;
  invoiceMasterId: string;
  onClose: () => void;
  onCancel: () => void;
  onSuccess: () => void;
};
